import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Database, Search, Pencil, Trash2, X, History, Info, ShieldCheck, Plus } from "lucide-react";
import { AsideSection } from "@/components/AsideSection";

const COLOUR_MAP = {
  red: "#ef4444", "red-lighter": "#f87171", "red-darker": "#b91c1c",
  green: "#22c55e", "green-light": "#4ade80", "green-dark": "#15803d",
  "green-lighter": "#86efac",
  blue: "#3b82f6", "blue-dark": "#1d4ed8", "blue-lighter": "#93c5fd",
  yellow: "#eab308", "yellow-lighter": "#fde047", "yellow-darker": "#a16207",
  purple: "#8b5cf6", "purple-light": "#a78bfa", "purple-lighter": "#c4b5fd",
  "purple-darker": "#6d28d9",
  gray: "#94a3b8", "gray-lighter": "#cbd5e1", "gray-darker": "#475569",
};

const BOOL_COLUMNS = new Set(["include_in_asset_totals", "include_in_depot_totals", "sync"]);

function humanise(col) {
  return col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Cell({ column, value }) {
  if (value === "" || value == null) return <span className="text-slate-300">—</span>;
  if (column === "colour" && COLOUR_MAP[value]) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full ring-1 ring-black/5" style={{ backgroundColor: COLOUR_MAP[value] }} />
        <span className="text-slate-600">{value}</span>
      </span>
    );
  }
  if (BOOL_COLUMNS.has(column) && (value === "1" || value === "0")) {
    return value === "1"
      ? <span className="text-emerald-600 font-medium">Yes</span>
      : <span className="text-slate-400">No</span>;
  }
  return <span className="text-slate-700">{value}</span>;
}

export default function DatabasesPage() {
  const { key } = useParams();
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sections, setSections] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [recordRow, setRecordRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/databases/${key}`)
      .then((r) => setTable(r.data))
      .catch(() => setTable(null))
      .finally(() => setLoading(false));
  }, [key]);

  useEffect(() => { setQ(""); load(); }, [load]);
  useEffect(() => {
    if (table?.supports_sections && !sections.length) {
      api.get("/databases/meta/sections").then((r) => setSections(r.data.sections)).catch(() => {});
    }
  }, [table, sections.length]);

  const rows = (table?.rows || []).filter((row) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return Object.values(row).some((v) => String(v).toLowerCase().includes(needle));
  });
  const canManagePerms = table?.can_manage_perms;
  const canManageData = table?.can_manage_data;
  const canEditRow = (row) => (row._linked ? (canManagePerms && !row._locked) : canManageData);
  const showActions = table?.editable;
  const cols = table?.columns || [];

  return (
    <div className="p-6 lg:p-8" data-testid="databases-page">
      <div className="flex items-start gap-3 mb-6">
        <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Database className="w-5 h-5" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{table?.label || humanise(key || "")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{table?.supports_sections ? "Role permissions · single source of truth" : (showActions ? "Reference data" : "Read-only reference data")} · {loading ? "…" : `${table?.rows?.length || 0} record${(table?.rows?.length || 0) === 1 ? "" : "s"}`}</p>
        </div>
        {!loading && table && (
          <div className="flex items-center gap-2 shrink-0">
            {canManageData && !table.supports_sections && (
              <Button size="sm" className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setAddOpen(true)} data-testid="db-add-btn">
                <Plus className="w-4 h-4" /> Add record
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowHistory(true)} data-testid="db-history-btn">
              <History className="w-4 h-4" /> Change history
            </Button>
          </div>
        )}
      </div>

      {!loading && table && (
        <div className="relative mb-3 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid="databases-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter records…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300" />
        </div>
      )}

      {loading ? (
        <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading…</div>
      ) : !table ? (
        <div className="text-center text-sm text-slate-400 py-16 bg-white rounded-2xl border border-dashed border-slate-200">Reference table not found.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="databases-table">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {cols.map((c) => <th key={c} className="px-4 py-3 whitespace-nowrap">{humanise(c)}</th>)}
                <th className="px-4 py-3 whitespace-nowrap">Description</th>
                {table.supports_sections && <th className="px-4 py-3 whitespace-nowrap">Can view</th>}
                {showActions && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50 align-top" data-testid={`databases-row-${i}`}>
                  {cols.map((c) => (
                    <td key={c} className="px-4 py-2.5 max-w-[420px] truncate" title={String(row[c] ?? "")}>
                      <Cell column={c} value={row[c]} />
                    </td>
                  ))}
                  <td className="px-4 py-2.5 max-w-[280px] text-slate-500 text-xs" title={row.description || ""}>
                    {row.description ? <span className="line-clamp-2">{row.description}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  {table.supports_sections && (
                    <td className="px-4 py-2.5">
                      {row._linked
                        ? ((row.sections || []).length
                            ? <span className="text-xs text-slate-500">{row.sections.length} section{row.sections.length === 1 ? "" : "s"}</span>
                            : <span className="text-slate-300 text-xs">—</span>)
                        : <span className="text-[10px] uppercase tracking-wide text-slate-400">legacy</span>}
                    </td>
                  )}
                  {showActions && (
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        {row._locked ? (
                          <span className="text-[11px] text-slate-400 italic">Super Admin (locked)</span>
                        ) : canEditRow(row) ? (
                          <>
                            <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => (key === "role-types" ? setEditRow(row) : setRecordRow(row))} data-testid={`db-edit-${row.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</Button>
                            {!row._linked && (
                              <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setDeleteRow(row)} data-testid={`db-delete-${row.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            )}
                          </>
                        ) : (
                          <span className="text-[11px] text-slate-300">—</span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={cols.length + 1 + (showActions ? 1 : 0) + (table.supports_sections ? 1 : 0)} className="px-4 py-10 text-center text-slate-400">No records match “{q}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editRow && (
        <EditModal tableKey={key} row={editRow} supportsSections={table.supports_sections} sections={sections}
          onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />
      )}
      {(addOpen || recordRow) && (
        <RecordForm tableKey={key} columns={cols} row={recordRow}
          onClose={() => { setAddOpen(false); setRecordRow(null); }}
          onSaved={() => { setAddOpen(false); setRecordRow(null); load(); }} />
      )}
      {deleteRow && (
        <ConfirmDelete row={deleteRow} tableKey={key} onClose={() => setDeleteRow(null)} onDone={() => { setDeleteRow(null); load(); }} />
      )}
      {showHistory && <HistoryModal tableKey={key} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function HistoryModal({ tableKey, onClose }) {
  const [entries, setEntries] = useState(null);
  useEffect(() => { api.get(`/databases/${tableKey}/history`).then((r) => setEntries(r.data.entries)).catch(() => setEntries([])); }, [tableKey]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[1px] flex justify-end" onClick={onClose} data-testid="db-history-modal">
      <div className="relative bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-hidden flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 hover:bg-slate-50"><X className="w-5 h-5" /></button>
        <div className="flex items-center px-6 py-5 border-b border-slate-100 pr-16">
          <h3 className="text-xl font-bold text-slate-900">Change history</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {!entries ? <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            : entries.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No changes recorded yet.</p>
            : (
            <ol className="space-y-3">
              {entries.map((e, i) => (
                <li key={i} className="border-l-2 border-indigo-200 pl-3" data-testid={`db-history-${i}`}>
                  <div className="text-sm font-medium text-slate-800">{e.detail}</div>
                  <div className="text-xs text-slate-400">{e.actor_email} · {new Date(e.ts).toLocaleString("en-GB")}</div>
                  {e.before && e.after && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      <span className="text-rose-500">−{e.before.length}</span> → <span className="text-emerald-600">+{e.after.length}</span> permissions
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function EditModal({ tableKey, row, supportsSections, sections, onClose, onSaved }) {
  const [title, setTitle] = useState(row.title || "");
  const [description, setDescription] = useState(row.description || "");
  const [sel, setSel] = useState(new Set(row.sections || []));
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setSel((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/databases/${tableKey}/${row.id}`, { title, description, sections: [...sel] });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[1px] flex justify-end" onClick={onClose} data-testid="db-edit-modal">
      <div className="relative bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-hidden flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 hover:bg-slate-50"><X className="w-5 h-5" /></button>
        <div className="flex items-center px-6 py-5 border-b border-slate-100 pr-16">
          <h3 className="text-xl font-bold text-slate-900">Edit “{row.title}”</h3>
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          <AsideSection first icon={Info} title="Details" description="A name and short description for this record.">
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Title</span>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="db-edit-title" /></label>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Short description</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                placeholder="What is this for?"
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                data-testid="db-edit-description" /></label>
          </AsideSection>
          {supportsSections && (
            <AsideSection icon={ShieldCheck} title="Sections they can view"
              description={row._linked
                ? "Linked to the live role — changes take effect immediately."
                : "Legacy role type — not linked to a live RBAC role."}>
              <div className="grid grid-cols-2 gap-1.5">
                {sections.map((s) => (
                  <label key={s.key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-sm ${!row._linked ? "opacity-50 cursor-not-allowed" : sel.has(s.key) ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>
                    <Switch disabled={!row._linked} checked={sel.has(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`db-sec-${s.key}`} />
                    {s.label}{s.financial ? " ·finance" : ""}
                  </label>
                ))}
              </div>
            </AsideSection>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-white">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="db-edit-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDelete({ row, tableKey, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/databases/${tableKey}/${row.id}`);
      toast.success("Deleted");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[1px] flex justify-end" onClick={onClose} data-testid="db-delete-modal">
      <div className="relative bg-white h-full w-full sm:w-1/2 sm:max-w-none p-6 pt-16 shadow-2xl overflow-y-auto border-l border-slate-200 animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 hover:bg-slate-50"><X className="w-5 h-5" /></button>
        <h3 className="text-xl font-bold text-slate-900">Delete “{row.title}”?</h3>
        <p className="text-sm text-slate-500 mt-1">This removes the record from this reference table. You can re-import the source data to restore it.</p>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={del} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="db-delete-confirm">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Delete</Button>
        </div>
      </div>
    </div>
  );
}


function RecordForm({ tableKey, columns, row, onClose, onSaved }) {
  const isEdit = !!row;
  const editable = (columns || []).filter((c) => c !== "id");
  const [values, setValues] = useState(() => {
    const v = {};
    editable.forEach((c) => { v[c] = row ? (row[c] ?? "") : ""; });
    return v;
  });
  const [description, setDescription] = useState(row?.description || "");
  const [busy, setBusy] = useState(false);
  const set = (c, val) => setValues((s) => ({ ...s, [c]: val }));

  const save = async () => {
    if (columns.includes("title") && !String(values.title || "").trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/databases/${tableKey}/${row.id}`, { values, description });
      else await api.post(`/databases/${tableKey}`, { values, description });
      toast.success(isEdit ? "Saved" : "Record added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[1px] flex justify-end" onClick={onClose} data-testid="db-record-modal">
      <div className="relative bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-hidden flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 hover:bg-slate-50"><X className="w-5 h-5" /></button>
        <div className="flex items-center px-6 py-5 border-b border-slate-100 pr-16">
          <h3 className="text-xl font-bold text-slate-900">{isEdit ? `Edit “${row.title || "record"}”` : "Add record"}</h3>
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          <AsideSection first icon={Info} title="Record details" description="Fill in the fields for this reference record.">
            {editable.map((c) => (
              <label key={c} className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{humanise(c)}</span>
                {c === "colour" ? (
                  <select value={values[c] || ""} onChange={(e) => set(c, e.target.value)} data-testid={`db-field-${c}`}
                    className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300">
                    <option value="">—</option>
                    {Object.keys(COLOUR_MAP).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                ) : (
                  <Input value={values[c] || ""} onChange={(e) => set(c, e.target.value)} className="mt-1" data-testid={`db-field-${c}`} />
                )}
              </label>
            ))}
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Short description</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What is this for?"
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                data-testid="db-field-description" />
            </label>
          </AsideSection>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-white">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="db-record-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}{isEdit ? "Save" : "Add record"}</Button>
        </div>
      </div>
    </div>
  );
}
